import React, { useCallback, useEffect, useRef } from 'react';
import type { RefObject, Dispatch, SetStateAction } from 'react';

import Konva from 'konva';
import type { Context } from 'konva/lib/Context';
import { Group } from 'react-konva';

import useImage from 'use-image';

// models
import { SceneManager } from 'Components/CanvasEditor/models/SceneManager';
import { KonvaImage } from 'Components/CanvasEditor/models/KonvaImage';
import { BaseScene } from 'Components/CanvasEditor/models/BaseScene';

import type { ImageElement as ImageElementType } from 'Components/CanvasEditor/types';

interface CropperImageProps {
  stageRef: RefObject<Konva.Stage>;
  layerRef: RefObject<Konva.Layer>;
  sceneManager: [SceneManager | null, Dispatch<SetStateAction<SceneManager | null>>];
  setActiveCropper: Dispatch<SetStateAction<boolean>>;
  element: ImageElementType;
  onElementChange: (props: Partial<ImageElementType>, id: string, saveToHistory?: boolean) => void;
  onSelectElement: (id: string | null) => void;
  selectedId: string | null;
}

export default function CropperImage({
  stageRef,
  layerRef,
  sceneManager: [sceneManager, setSceneManager],
  setActiveCropper,
  element,
  selectedId,
  onElementChange,
  onSelectElement,
}: CropperImageProps) {
  const imageViewRef = useRef<Konva.Group>(null);

  const [img] = useImage(element.src, 'anonymous');

  const syncKonvaToElement = useCallback(
    (konvaImage: KonvaImage, elementId?: string) => {
      const id = elementId || konvaImage.id();

      onElementChange(
        {
          x: konvaImage.x(),
          y: konvaImage.y(),
          width: konvaImage.width(),
          height: konvaImage.height(),
          rotation: konvaImage.rotation(),
          cropX: konvaImage.cropX(),
          cropY: konvaImage.cropY(),
          cropWidth: konvaImage.cropWidth(),
          cropHeight: konvaImage.cropHeight(),
        },
        id,
        true
      );
    },
    [onElementChange]
  );

  const sceneFunc = useCallback(
    (ctx: Context, shape) => {
      const { x, y, width, height } = shape.crop();
      const w = shape.width();
      const h = shape.height();

      ctx.save();

      if (element.mask === 'circle') {
        ctx.beginPath();
        ctx.ellipse(w / 2, h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();
      }

      ctx.drawImage(shape.image(), x, y, width, height, 0, 0, w, h);
      ctx.restore();

      ctx.fillStrokeShape(shape);
    },
    [element.mask]
  );

  useEffect(() => {
    if (sceneManager) {
      const konvaImage = imageViewRef.current?.findOne(`#${element.id}`);
      if (!konvaImage || konvaImage.id() !== selectedId) return;

      const baseScene = sceneManager.getScene(BaseScene) as InstanceType<typeof BaseScene>;
      baseScene.setSelection([konvaImage]);
    }
  }, [selectedId]);

  useEffect(() => {
    const layer = imageViewRef.current?.getLayer()!;
    const konvaImage = imageViewRef.current?.findOne(`#${element.id}`);
    if (!konvaImage || konvaImage.id() !== selectedId) return;

    konvaImage.setAttr('sceneFunc', sceneFunc);
    layer.batchDraw();
  }, [element.mask]);

  useEffect(() => {
    if (stageRef.current && layerRef.current) {
      const stage = stageRef.current;
      const layer = layerRef.current;

      let newManager = sceneManager;
      if (!newManager) {
        newManager = new SceneManager(stage, { id: 'scene-manager' });
        layer.add(newManager);

        setSceneManager(newManager);
      }

      newManager.goto(BaseScene, { syncCallback: syncKonvaToElement, setActiveCropper });
    }
  }, []);

  useEffect(() => {
    if (stageRef.current && layerRef.current && imageViewRef.current && img && sceneManager) {
      if (imageViewRef.current!.findOne(`#${element.id}`)) return;

      const { width } = element;
      const { height } = element;
      const aspectRatio = width / height;

      let cropWidth: number;
      let cropHeight: number;

      const imageRatio = img.width / img.height;

      if (aspectRatio >= imageRatio) {
        cropWidth = img.width;
        cropHeight = img.width / aspectRatio;
      } else {
        cropWidth = img.height * aspectRatio;
        cropHeight = img.height;
      }

      const cropX = (img.width - cropWidth) / 2;
      const cropY = (img.height - cropHeight) / 2;

      const konvaImage = new KonvaImage({
        image: img,
        id: element.id,
        name: 'object',
        visible: element.visible,
        draggable: element.draggable,
        cornerRadius: element.cornerRadius,
        rotation: element.rotation,
        x: element.x,
        y: element.y,
        width: element.width,
        height: element.height,
        cropX,
        cropY,
        cropWidth,
        cropHeight,
        scaleX: 1,
        scaleY: 1,
        sceneFunc,
      });

      // Add event listeners to sync Konva changes with element state
      konvaImage.on('dragend', () => {
        syncKonvaToElement(konvaImage, element.id);
      });

      konvaImage.on('transformend', () => {
        syncKonvaToElement(konvaImage, element.id);
      });

      konvaImage.on('click', () => {
        onSelectElement(element.id);
      });

      imageViewRef.current!.add(konvaImage);
    }
  }, [img, sceneManager]);

  return <Group id={`image-view-${element.id}`} ref={imageViewRef} />;
}
